// ============================================
// ChurnZonesSettingsMenu.jsx - settings for the Churn Zones overlay.
// Shared shell (menu/ChartMenu) only. Every control applies immediately
// (no Apply); numbers are debounced MenuNumbers. Sections fold and remember
// their open state. config is the normalized churn config (utils/churnZones).
// RESET restores CHURN_ZONE_DEFAULTS (detection, zones, style); leaves the
// overlay on/off toggle alone.
// ============================================
import ChartMenu, { MenuSection, MenuRow, MenuNumber, MenuSwatch, MenuToggle, MenuDivider, MenuSelect, MenuActions, MenuButton } from './menu/ChartMenu'
import { CHURN_ZONE_DEFAULTS } from '../utils/churnZones'

const STYLES = [
  { value: 'boxes', label: 'Boxes' },
  { value: 'background', label: 'Background' },
]

const SK = 'solcheddar:churnMenu.'

export default function ChurnZonesSettingsMenu({ show, x, y, openLeft, enabled, onToggle, config, onUpdate, onClose }) {
  if (!show || !config) return null
  const c = config
  const set = (k) => (v) => onUpdate({ [k]: v })
  const resetAll = () => onUpdate({ ...CHURN_ZONE_DEFAULTS })
  return (
    <ChartMenu anchor={{ x, y }} openLeft={openLeft} onClose={onClose} title="Churn Zones">
      <MenuRow label="Churn">
        <MenuToggle on={!!enabled} onChange={(v) => onToggle(v)} title="Show / hide churn zones">
          {enabled ? 'ON' : 'OFF'}
        </MenuToggle>
        <MenuDivider />
        <MenuToggle on={c.showUnresolved} onChange={set('showUnresolved')} title="Show zones that have not broken out">
          Unresolved
        </MenuToggle>
      </MenuRow>

      <MenuSection title="Detection" collapsible storageKey={SK + 'detect'}>
        <MenuRow>
          <MenuNumber label="VOL x" value={c.relVolMin} float step={0.1} min={0.1} max={20} width={44}
            title="Volume must be this multiple of the average" onCommit={set('relVolMin')} />
          <MenuDivider />
          <MenuNumber label="AVG" value={c.volLen} min={2} max={500} width={44}
            title="Bars in the volume / range average" onCommit={set('volLen')} />
        </MenuRow>
        <MenuRow>
          <MenuNumber label="RANGE" value={c.rangeRatio} float step={0.05} min={0.05} max={2} width={44}
            title="Range must be at most this fraction of the expected range" onCommit={set('rangeRatio')} />
          <MenuDivider />
          <MenuNumber label="FIT" value={c.fitLen} min={10} max={1000} width={44}
            title="Bars used to fit expected range vs volume" onCommit={set('fitLen')} />
        </MenuRow>
        <MenuRow>
          <MenuNumber label="BAL" value={c.balanceMin} float step={0.05} min={0} max={1} width={44}
            title="Buy/sell balance needed (1 = perfectly even)" onCommit={set('balanceMin')} />
        </MenuRow>
      </MenuSection>

      <MenuSection title="Zones" collapsible storageKey={SK + 'zones'}>
        <MenuRow>
          <MenuNumber label="MERGE" value={c.mergeGap} min={0} max={50} width={40}
            title="Merge churn bars within this many bars" onCommit={set('mergeGap')} />
          <MenuDivider />
          <MenuNumber label="EXPIRE" value={c.expireBars} min={1} max={1000} width={44}
            title="Drop unresolved zones after this many bars" onCommit={set('expireBars')} />
        </MenuRow>
        <MenuRow>
          <MenuNumber label="BREAK x" value={c.breakoutMult} float step={0.1} min={0} max={10} width={44}
            title="Breakout needs range (or distance) of this multiple of the average range" onCommit={set('breakoutMult')} />
        </MenuRow>
      </MenuSection>

      <MenuSection title="Style" collapsible storageKey={SK + 'style'}>
        <MenuRow label="Draw">
          <MenuSelect value={c.style} options={STYLES} title="Boxes over the chart, or tint the footprint background of the zone's candles"
            onChange={set('style')} />
        </MenuRow>
        <MenuRow>
          <MenuSwatch label="UP" value={c.upCol} onChange={set('upCol')} />
          <MenuDivider />
          <MenuSwatch label="DOWN" value={c.downCol} onChange={set('downCol')} />
          <MenuDivider />
          <MenuSwatch label="OPEN" value={c.noneCol} onChange={set('noneCol')} />
        </MenuRow>
        <MenuRow>
          <MenuNumber label={c.style === 'background' ? 'TINT' : 'OPACITY'} value={c.opacity} float step={0.05} min={0} max={1} width={44}
            title="Box fill opacity (0-1)" onCommit={set('opacity')} />
        </MenuRow>
      </MenuSection>

      <MenuActions>
        <MenuButton variant="ghost" style={{ flex: 1 }} onClick={resetAll}>RESET</MenuButton>
        <MenuButton variant="primary" onClick={onClose}>CLOSE</MenuButton>
      </MenuActions>
    </ChartMenu>
  )
}
